// prettier-ignore-start
// Theme import order is important
import "bootstrap";
import "./assets/theme.css";
// prettier-ignore-end
import PrimeVue from "primevue/config";
import { App, Plugin, createApp, watch } from "vue";
import MainComponent from "./App.vue";
import primeVuePassthrough from "./assets/primevue-passthrough";
import { i18n } from "./i18n";
import { buildPrimeVueLocale } from "./i18n/primeVueLocale";
import "./assets/primevue-unstyled.css";
import "./assets/utilities.scss";
import "./assets/brands/ekoenergy.scss";
import { applyBranding } from "./branding";
import router from "./router";
import { followAccountLanguage } from "./composables/account/useAccountStore";
// Import FontAwesome
import {
  faArrowLeft as faArrowLeftRegular,
  faArrowRight as faArrowRightRegular,
  faArrowUpRightFromSquare as faArrowUpRightFromSquareRegular,
  faBan as faBanRegular,
  faBuilding as faBuildingRegular,
  faCartShopping as faCartShoppingRegular,
  faCertificate as faCertificateRegular,
  faCheck as faCheckRegular,
  faCheckDouble as faCheckDoubleRegular,
  faCircleInfo as faCircleInfoRegular,
  faCircleXmark as faCircleXmarkRegular,
  faDollarSign as faDollarSignRegular,
  faDownload as faDownloadRegular,
  faEye as faEyeRegular,
  faHourglassHalf as faHourglassHalfRegular,
  faInbox as faInboxRegular,
  faLayerGroup as faLayerGroupRegular,
  faListUl as faListUlRegular,
  faPaperPlane as faPaperPlaneRegular,
  faRotateLeft as faRotateLeftRegular,
  faXmark as faXmarkRegular,
} from "@awesome.me/kit-00d73b0c5d/icons/classic/regular";
import {
  faAnglesDown,
  faArrowDown,
  faArrowLeft,
  faArrowRight,
  faArrowsMaximize,
  faArrowUp,
  faArrowUpRightFromSquare,
  faArrowsUpToLine,
  faBan,
  faBell,
  faBellSlash,
  faBolt,
  faBomb,
  faBridgeWater,
  faBriefcase,
  faBuilding,
  faCalendar,
  faCalculator,
  faCalendarAlt,
  faCalendarCheck,
  faCalendarDays,
  faCaretDown,
  faCaretUp,
  faCertificate,
  faChartColumn,
  faChartLine,
  faChartPie,
  faCheck,
  faCheckCircle,
  faCheckSquare,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faChevronUp,
  faCircleCheck,
  faCircleDashed,
  faCircleExclamation,
  faCircleInfo,
  faClock,
  faCloudUpload,
  faCloudUploadAlt,
  faCodeBranch,
  faCog,
  faCogs,
  faComment,
  faCompressAlt,
  faComputerMouseScrollwheel,
  faClone,
  faCopy,
  faDollarSign,
  faDownload,
  faEdit,
  faEllipsisV,
  faEnvelope,
  faExchangeAlt,
  faExclamationCircle,
  faExclamationTriangle,
  faExpandArrowsAlt,
  faExternalLink,
  faExternalLinkAlt,
  faEye,
  faFile,
  faFileAlt,
  faFileArrowUp,
  faFileCheck,
  faFileCircleCheck,
  faFileContract,
  faFileExport,
  faFileInvoice,
  faFileInvoiceDollar,
  faFilePdf,
  faFileSignature,
  faFileSlash,
  faFilter,
  faFilterCircleXmark,
  faFire,
  faFlag,
  faFolderOpen,
  faGavel,
  faGear,
  faGlobe,
  faHand,
  faHandPaper,
  faHandshake,
  faHashtag,
  faHistory,
  faHourglassHalf,
  faIdCard,
  faInbox,
  faInfoCircle,
  faKey,
  faLayerGroup,
  faLeaf,
  faLightbulb,
  faLinkHorizontalSlash,
  faList,
  faListUl,
  faLock,
  faMagic,
  faMagnifyingGlass,
  faMagnifyingGlassChart,
  faPaperPlane,
  faPaperclip,
  faPenToSquare,
  faPencil,
  faPencilAlt,
  faPercentage,
  faPlay,
  faPlug,
  faPlus,
  faPlusCircle,
  faQuestionCircle,
  faRadiation,
  faReceipt,
  faRectangleList,
  faRecycle,
  faRedo,
  faRightToBracket,
  faRobot,
  faRotateLeft,
  faSave,
  faSearch,
  faServer,
  faShareSquare,
  faSheetPlastic,
  faShieldHalved,
  faShoppingCart,
  faSignature,
  faSlidersH,
  faSolarPanel,
  faSort,
  faSortDown,
  faSortUp,
  faSparkles,
  faSpinner,
  faSquareBolt,
  faStickyNote,
  faSync,
  faTableColumns,
  faTimes,
  faTimesCircle,
  faTrash,
  faTrashAlt,
  faUser,
  faUserPlus,
  faUserShield,
  faUserTie,
  faWarehouse,
  faWarning,
  faWater,
  faWindTurbine,
  faXmark,
} from "@awesome.me/kit-00d73b0c5d/icons/classic/solid";
import { library } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { createPinia } from "pinia";
import vueCountryRegionSelect from "vue3-country-region-select";
import { auth0 } from "./auth";
import logger, { logComponentError } from "./logger";

// Add all solid icons to the library
// You can also import specific icons if preferred
library.add(
  faAnglesDown,
  faArrowDown,
  faArrowLeft,
  faArrowRight,
  faArrowsMaximize,
  faArrowUp,
  faArrowUpRightFromSquare,
  faArrowsUpToLine,
  faBan,
  faBell,
  faBellSlash,
  faBolt,
  faBomb,
  faBridgeWater,
  faBriefcase,
  faBuilding,
  faCalendar,
  faCalculator,
  faCalendarAlt,
  faCalendarCheck,
  faCalendarDays,
  faCaretDown,
  faCaretUp,
  faCertificate,
  faChartColumn,
  faChartLine,
  faChartPie,
  faCheck,
  faCheckCircle,
  faCheckSquare,
  faChevronDown,
  faChevronLeft,
  faChevronUp,
  faChevronRight,
  faCircleCheck,
  faCircleDashed,
  faCircleExclamation,
  faCircleInfo,
  faClock,
  faCloudUpload,
  faCloudUploadAlt,
  faCodeBranch,
  faCog,
  faCogs,
  faCompressAlt,
  faComputerMouseScrollwheel,
  faClone,
  faCopy,
  faComment,
  faDollarSign,
  faDownload,
  faEdit,
  faEllipsisV,
  faEnvelope,
  faExchangeAlt,
  faExclamationCircle,
  faExclamationTriangle,
  faExpandArrowsAlt,
  faExternalLink,
  faExternalLinkAlt,
  faEye,
  faFile,
  faFileAlt,
  faFileArrowUp,
  faFileCheck,
  faFileCircleCheck,
  faFileContract,
  faFileExport,
  faFileInvoice,
  faFileInvoiceDollar,
  faFilePdf,
  faFileSignature,
  faFileSlash,
  faFilter,
  faFilterCircleXmark,
  faFire,
  faFlag,
  faFolderOpen,
  faGavel,
  faGear,
  faGlobe,
  faHand,
  faHandPaper,
  faHandshake,
  faHashtag,
  faHistory,
  faHourglassHalf,
  faIdCard,
  faInbox,
  faInfoCircle,
  faKey,
  faLayerGroup,
  faLeaf,
  faLightbulb,
  faLinkHorizontalSlash,
  faList,
  faListUl,
  faLock,
  faMagic,
  faMagnifyingGlass,
  faMagnifyingGlassChart,
  faPaperPlane,
  faPaperclip,
  faPenToSquare,
  faPencil,
  faPencilAlt,
  faPercentage,
  faPlay,
  faPlug,
  faPlus,
  faPlusCircle,
  faQuestionCircle,
  faRadiation,
  faReceipt,
  faRectangleList,
  faRecycle,
  faRedo,
  faRightToBracket,
  faRobot,
  faRotateLeft,
  faSave,
  faSearch,
  faServer,
  faShareSquare,
  faSheetPlastic,
  faShieldHalved,
  faShoppingCart,
  faSignature,
  faSlidersH,
  faSolarPanel,
  faSort,
  faSortDown,
  faSortUp,
  faSparkles,
  faSpinner,
  faSquareBolt,
  faStickyNote,
  faSync,
  faTableColumns,
  faTimes,
  faTimesCircle,
  faTrash,
  faTrashAlt,
  faUser,
  faUserPlus,
  faUserShield,
  faUserTie,
  faWarehouse,
  faWarning,
  faWater,
  faWindTurbine,
  faXmark,
  faArrowLeftRegular,
  faArrowRightRegular,
  faArrowUpRightFromSquareRegular,
  faBanRegular,
  faBuildingRegular,
  faCartShoppingRegular,
  faCertificateRegular,
  faCheckRegular,
  faCheckDoubleRegular,
  faCircleInfoRegular,
  faCircleXmarkRegular,
  faDollarSignRegular,
  faDownloadRegular,
  faEyeRegular,
  faHourglassHalfRegular,
  faInboxRegular,
  faLayerGroupRegular,
  faListUlRegular,
  faPaperPlaneRegular,
  faRotateLeftRegular,
  faXmarkRegular
);

applyBranding();

const app: App<Element> = createApp(MainComponent);

// Register FontAwesomeIcon component globally
app.component("FontAwesomeIcon", FontAwesomeIcon);

const pinia = createPinia();

app.use(router);
app.use(vueCountryRegionSelect);
app.use(auth0 as unknown as Plugin);
app.use(i18n);
app.use(pinia);
followAccountLanguage(router);
app.use(PrimeVue, {
  unstyled: true,
  pt: primeVuePassthrough,
  locale: buildPrimeVueLocale(
    (key) => i18n.global.t(key),
    i18n.global.locale.value
  ),
  zIndex: {
    modal: 1100,
    overlay: 1100,
    menu: 1100,
    tooltip: 1100,
  },
});

watch(
  () => i18n.global.locale.value,
  () => {
    const primevue = (app.config.globalProperties as any).$primevue;
    if (primevue?.config) {
      primevue.config.locale = buildPrimeVueLocale(
        (key) => i18n.global.t(key),
        i18n.global.locale.value
      );
    }
  }
);
app.config.errorHandler = logComponentError;

if ((import.meta as any).env?.DEV) {
  window.addEventListener(
    "error",
    (e: ErrorEvent) => {
      if (
        typeof e.message === "string" &&
        e.message.includes("ResizeObserver loop")
      ) {
        e.stopImmediatePropagation();
      }
    },
    true
  );
}

app.mount("#app");

window.onerror = (message, source, lineno, colno, error) => {
  logger.error("Global JavaScript error", {
    message,
    source,
    lineno,
    colno,
    error,
  });
};

window.onunhandledrejection = (event) => {
  logger.error("Unhandled promise rejection", { reason: event.reason });
};
